import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CaretLeft, ImageSquare } from '@phosphor-icons/react';
import { Typography, useMediaQuery, useTheme } from '@mui/material';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { Loader, Spacer } from 'theSource/components';
import { CompareCarrierLogoBoxStyled } from '../PlanCard/PlanCardStyled';
import {
  ComparePlansStyled,
  PremiumTextStyled,
} from '../PlanCard/PlanDetailsStyled';
import { ComparisonSummary } from './ComparisonSummary';
import { ROUTES } from 'enrollment/app/routes';
import {
  AdditionalPlanElectionDataDto,
  ElectRequestDto,
} from '../PlanCard/types';
import { queryKeyGenerator, useApiMutation } from 'theSource/utils';
import { ElectResponseDto } from '../Volume/types';
import { ErrorResponseDto } from 'enrollment/types/dtos';
import {
  UPDATE_ELECTION_QUERY_KEY,
  buildElectionParams,
} from '../PlanCard/utils';
import { useConfigurationQuery } from 'theSource/queries';
import {
  BenefitPlanDto,
  BenefitPlansDto,
  CoverageDto,
} from 'enrollment/queries/useBenefitPlansQuery/types';
import {
  ComparePlansSummaryStyled,
  MobileFilterHeaderStyled,
  MobileFilterSectionStyled,
  PlanNameStyled,
} from './ComparePlansStyled';
import { PsButtonTuner } from 'stencilSetup';
import { ComparisonKeys } from './ComparisonKeys';
import { TooltipWrapper } from 'enrollment/components/Shared/TooltipWrapper';
import '../../Plans/PlanSelect.css';
import { selectPerPayPeriodText } from 'enrollment/services';
import { getPremiumStyled, RETURN_DISPLAY } from 'enrollment/utils';
import { selectEnrollmentOrgPlanYearId } from 'theSource/features/Home/components/EnrollmentBanner/services';
import { selectIsAdmin } from 'theSource/features';
import { getShowEsignModal } from '../utils';
import { EsignatureModal } from '../EsignatureModal';
import { openEsignatureModal } from '../EsignatureModal/tests/utils';

export interface ComparePlansDataState {
  state: {
    currentBenefitCategory?: string;
    benefitData?: BenefitPlansDto;
    benefit?: BenefitPlanDto;
    contextLookup?: string;
    comparePlans?: any[];
    selectedDependentIds?: number[];
    orgBenefitId?: number;
    coverage?: CoverageDto;
  };
}

export const ComparePlans = () => {
  const { t } = useTranslation('pages.enrollmentComparePlans');
  const perPayText = useSelector(selectPerPayPeriodText);
  let orgPlanYearContextId = useSelector(selectEnrollmentOrgPlanYearId);

  const [sticky, setSticky] = useState(false);

  const { configuration } = useConfigurationQuery();

  const apiHost = configuration?.apiHost;
  const navigate = useNavigate();

  const location = useLocation() as any;
  const comparePlans = location.state?.comparePlans;
  const benefit = location.state?.benefit;
  const orgBenefitId = location.state?.orgBenefitId;
  const coverage = location.state?.coverage;
  const currentBenefitCategory = location.state?.currentBenefitCategory;
  const selectedDependentIds = location.state?.selectedDependentIds;
  const benefitData = location.state?.benefitData;
  const contextLookup = location.state?.contextLookup;
  orgPlanYearContextId =
    contextLookup === 'life_event' ? orgPlanYearContextId : 0;
  const [openMobileFilter, setOpenMobileFilter] = useState(false);
  const theme = useTheme();
  const isScreenSmall = useMediaQuery(theme.breakpoints.down(1200));
  const isMobile = window.innerWidth < 744;
  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    className: 'center mt-3',
    centerMode: true,
    slidesToShow: isMobile ? 1 : 1.4,
    slidesToScroll: 1,
    arrows: false,
  };
  const [comparisonKeys, setComparisonKeys] = useState<string[]>([]);
  const plansPagePath = `/enrollment${ROUTES.enrollment.plans.path}/${orgBenefitId}`;

  const isAdmin = useSelector(selectIsAdmin);

  const { isLoading, mutate: updateElection } = useApiMutation<
    ElectResponseDto,
    ErrorResponseDto,
    ElectRequestDto
  >({
    queryKey: UPDATE_ELECTION_QUERY_KEY,
    queryKeysToInvalidate: queryKeyGenerator(
      `${currentBenefitCategory}-${contextLookup}`,
      orgPlanYearContextId
    ),
    method: 'put',
    endpoint: `${apiHost}/v2/self_service/benefits/elect`,
    onSuccess: () => {
      navigate(`/enrollment/${ROUTES.enrollment.benefits.path}`);
    },
    getErrorMessage: (error) => error.response?.data?.details,
    showSuccessSnackbar: false,
  });

  const emptyFunction = () => {
    return;
  };

  const handleElection = (plan: any) => {
    if (getShowEsignModal(plan?.esign_data, coverage, isAdmin)) {
      openEsignatureModal(plan?.plan_id);
    } else {
      updateElection(
        buildElectionParams({
          plan: plan,
          levelId: plan.level_id,
          orgBenefitId,
          enrollmentContextType: contextLookup,
          selectedDependentIds,
        })
      );
    }
  };

  const esignHandleElection = (
    plan: any,
    additionalPlanData?: AdditionalPlanElectionDataDto
  ) => {
    updateElection(
      buildElectionParams({
        plan: plan,
        levelId: plan.level_id,
        orgBenefitId,
        enrollmentContextType: contextLookup,
        selectedDependentIds,
        additionalPlanData,
      })
    );
  };

  const renderEsignatureModals = () => {
    return (
      <div>
        {comparePlans?.map((plan: BenefitPlanDto) => {
          const showEsignatureModal = getShowEsignModal(
            plan?.esign_data,
            coverage,
            isAdmin
          );
          return (
            <>
              {showEsignatureModal && (
                <EsignatureModal
                  setEsignElectionData={(additionalPlanData) =>
                    esignHandleElection(plan, additionalPlanData)
                  }
                  carrierLogoUrl={plan?.carrier_logo_url}
                  carrierName={plan?.carrier}
                  data={plan?.esign_data}
                  planId={plan?.plan_id}
                  hideChoosePlanButton={true}
                  setModalRequired={emptyFunction}
                />
              )}
            </>
          );
        })}
      </div>
    );
  };

  const renderSelectPlan = (
    plan: BenefitPlanDto,
    t: any,
    isSticky: Boolean
  ) => {
    return (
      <div
        className={`button-container ${isSticky ? 'mt-2' : 'mt-3 d-lg-flex flex-grow-1 align-items-end'} ${isScreenSmall ? 'text-center mt-md-4_5' : ''}`}
      >
        <PsButtonTuner
          buttonId={`choose-plan-button-${plan?.plan_id}`}
          label={isScreenSmall ? t('choosePlan') : t('common:select')}
          level="secondary"
          onClick={() => handleElection(plan)}
          otherData={[
            {
              attribute: 'aria-describedby',
              value: `plan-id-${plan?.plan_id}`,
            },
          ]}
        />
      </div>
    );
  };

  const getComparisonKeys = (keys: string[]) => {
    if (JSON.stringify(keys) !== JSON.stringify(comparisonKeys)) {
      setComparisonKeys(keys);
    }
  };

  interface PlanNameComponentProps {
    children: React.ReactNode;
    plan?: number;
  }

  const PlanNameComponent: React.FC<PlanNameComponentProps> = ({
    children,
    plan,
  }) => {
    const planNameRef = useRef<HTMLDivElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);
    const ariaName = `plan-id-${plan}`;

    useEffect(() => {
      const element = planNameRef.current;
      if (element) {
        setIsTruncated(element.scrollHeight > element.clientHeight);
      }
    }, [children]);

    return (
      <>
        {isTruncated ? (
          <TooltipWrapper
            position="top"
            tooltipContent={children}
            allowWrap={true}
          >
            <PlanNameStyled ref={planNameRef} id={ariaName}>
              <div tabIndex={0}>{children}</div>
            </PlanNameStyled>
          </TooltipWrapper>
        ) : (
          <PlanNameStyled ref={planNameRef} id={ariaName}>
            {children}
          </PlanNameStyled>
        )}
      </>
    );
  };
  // TODO: move this to utils
  const displayPlanDetails = (
    plan: BenefitPlanDto,
    t: any,
    isSticky: boolean
  ) => {
    return (
      <div className="compare-plan-details d-lg-flex flex-column">
        <div className=" carrier-logo-box">
          {plan?.carrier_logo_url && plan?.carrier_logo_url !== null ? (
            <CompareCarrierLogoBoxStyled>
              <img
                src={plan.carrier_logo_url}
                alt={plan.carrier || t('defaultLogoAltText')}
                data-testid="carrier-logo"
              />
            </CompareCarrierLogoBoxStyled>
          ) : (
            <CompareCarrierLogoBoxStyled>
              <ImageSquare size={24} weight="duotone" color="#56638a" />
              <Typography color="#56638a">{plan.carrier}</Typography>
            </CompareCarrierLogoBoxStyled>
          )}
        </div>
        <div className="text-center text-lg-left position-relative">
          <div className="overline1 d-lg-none">{plan.carrier}</div>
          <PlanNameComponent plan={plan.plan_id}>
            <>
              <span className="text-capitalize semiBold">{`${benefit?.benefit_lookup_code}: `}</span>
              <span>{plan.plan_name}</span>
            </>
          </PlanNameComponent>

          <PremiumTextStyled>
            {(!benefitData?.benefit?.hide_cost_if_empty ||
              Number(plan.subscriber_premium) > 0) &&
              getPremiumStyled({
                premium: plan.subscriber_premium,
                valueOnly: RETURN_DISPLAY,
                perPayText: isScreenSmall ? undefined : perPayText,
              })}
          </PremiumTextStyled>
        </div>
        {!isScreenSmall && renderSelectPlan(plan, t, isSticky)}
      </div>
    );
  };

  useEffect(() => {
    // for toggling sticky vs non-sticky designs
    const el = document.querySelector('.stick-the-element');
    if (!el) {
      return;
    }
    const observer = new IntersectionObserver(
      ([e]) => setSticky(e.intersectionRatio < 1),
      { threshold: [1] }
    );

    observer.observe(el as Element);

    return () => {
      observer.unobserve(el as Element);
    };
  });

  // setting focus on popup contents for wcag tabbable focus
  useEffect(() => {
    if (openMobileFilter === true) {
      document.getElementById('compare-menu')?.focus();
    } else {
      document.getElementById('compare-filter-button')?.focus();
    }
  }, [openMobileFilter]);

  //setting escape key exit on popup contents for wcag
  useEffect(() => {
    const keyListener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenMobileFilter(false);
      }
    };
    document.addEventListener('keydown', (e) => keyListener(e));

    return () => document.removeEventListener('keydown', (e) => keyListener(e));
  });

  const mobileFilterIcon = () => {
    const buttonProps = {
      buttonId: 'compare-filter-button',
      label: 'Filter',
      icon: 'sliders-horizontal',
      onClick: () => {
        setOpenMobileFilter(!openMobileFilter);
      },
      level: 'secondary',
      ...(isMobile && { isIcon: true }), // Conditionally add the isIcon property
    };
    return (
      <PsButtonTuner
        {...buttonProps}
        aria-haspopup="true"
        aria-expanded={openMobileFilter}
        aria-controls="compare-menu"
      />
    );
  };

  const mobileFilterSection = () => {
    return (
      <div className={openMobileFilter ? 'd-block' : 'd-none'}>
        <MobileFilterSectionStyled
          id="compare-menu"
          role="dialog"
          aria-modal="true"
          tabIndex={0}
        >
          <MobileFilterHeaderStyled>
            <h4>{t('filter')}</h4>
            <PsButtonTuner
              buttonId="close-filter-popup"
              label="Close"
              level="tertiary"
              isIcon={true}
              icon="x"
              extraClass="blackIcon mb-1"
              onClick={() => {
                setOpenMobileFilter(false);
              }}
              autoFocus={true}
            />
          </MobileFilterHeaderStyled>
          <ComparisonKeys
            setComparisonKeys={getComparisonKeys}
            className="compare-keys"
          />
        </MobileFilterSectionStyled>
      </div>
    );
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <main id="mainContent">
          <div className="d-flex justify-content-between align-items-center m-4 mt-md-5 pt-md-5 mt-lg-4 pt-lg-2">
            <Link
              to={plansPagePath}
              data-testid="back-to-plans-button"
              className="d-flex align-items-center text-decoration-none py-2_5"
              state={{
                benefitData: benefitData,
                currentBenefitCategory: currentBenefitCategory,
                contextLookup: contextLookup,
              }}
            >
              <CaretLeft />
              <Spacer width={1} />
              {t('common:back')}
            </Link>
            {isScreenSmall && mobileFilterIcon()}
          </div>
          <h1 className="mx-4">{t('title')}</h1>
          {!isScreenSmall ? (
            <>
              <div className={`stick-the-element ${sticky ? 'is-pinned' : ''}`}>
                <ComparePlansSummaryStyled>
                  <div className="d-flex w-100 compared-plans-container">
                    {comparePlans?.map((plan: BenefitPlanDto) => (
                      <ComparePlansStyled>
                        {displayPlanDetails(plan, t, false)}
                      </ComparePlansStyled>
                    ))}
                  </div>
                </ComparePlansSummaryStyled>
              </div>
              <ComparisonSummary
                getComparisonKeys={getComparisonKeys}
                comparisonKeys={comparisonKeys}
                plansDetails={comparePlans}
              />
            </>
          ) : (
            <div>
              <Slider {...sliderSettings}>
                {comparePlans?.map((plan: BenefitPlanDto, index: number) => (
                  <div key={index} className="slide-inner">
                    <ComparePlansStyled>
                      {displayPlanDetails(plan, t, false)}
                    </ComparePlansStyled>
                    {renderSelectPlan(plan, t, false)}
                    <ComparisonSummary
                      getComparisonKeys={getComparisonKeys}
                      comparisonKeys={comparisonKeys}
                      plansDetails={comparePlans}
                      currentPlan={plan}
                    />
                  </div>
                ))}
              </Slider>
            </div>
          )}
          <>{isScreenSmall && mobileFilterSection()}</>
        </main>
      )}
      {renderEsignatureModals()}
    </>
  );
};
